import { AccountContext } from 'app/core/lib/account-context';
import { AuthService as UMAuthService } from 'app/um/services/auth-service';
import { AuthService as CDAuthService } from 'app/cd/services/auth-service';
import { IssueService as CSIssueService } from 'app/cs/services/issue-service'
import { CaseService as CMCaseService } from 'app/cm/services/case-service';
import { ClaimService as CDClaimService } from 'app/cd/services/claim-service';
import { CampaignService as PMCampaignService } from 'app/pm/services/campaign-service'
import { openAuthSummary as umOpenAuthSummary } from 'app/um/components/summary/auth-summary';
import { openAuthSummary as cdOpenAuthSummary } from 'app/cd/components/summary/auth/auth-summary';
import { openCaseSummary as cmOpenCaseSummary } from 'app/cm/components/summary/case-summary';
import { openHouseholdCaseSummary as cmOpenHouseholdCaseSummary } from 'app/cm/components/summary/household-case-summary';
import { openClaimSummary as cdOpenClaimSummary } from 'app/cd/components/summary/claim/claim-summary';
import { autoinject, observable } from 'aurelia-framework';
import { TransitionalManager } from 'app/shared/lib/transitional/manager';
import * as UM from 'app/um/models/essette-api';
import * as CD from 'app/cd/models/essette-api';
import * as CS from 'app/cs/models/essette-api';
import * as CM from 'app/cm/models/essette-api';
import * as PM from 'app/pm/models/essette-api';
import { State } from 'app/shared/lib/essette-site';
import { apiCall } from 'app/shared/lib/essette-api';


const csIssueSummaryRouteID: string = '2007';
const csCallSummaryRouteID: string = '2006';
const agCaseSummaryRouteID: string = '6004';
const pmCandidateSummaryRouteID: string = '14007';

@autoinject
export class SummaryLoader {


    ////****************** Fields and Properties ************************************////
    @observable
    protected state: State = new State;

    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly umAuthService: UMAuthService,
        private readonly cdAuthService: CDAuthService,
        private readonly csIssueService: CSIssueService,
        private readonly cmCaseService: CMCaseService,
        private readonly cdClaimService: CDClaimService,
        private readonly pmCampaignService: PMCampaignService,
        private readonly transitional: TransitionalManager,

        ...rest: any[]) { }


    ////****************** Functions ************************************************////

    /**
     * Generates tooltips for summary links
     * @param code (attachedTo.Type.code)
     * @returns tooltip resource key
     */
    public attachedToToolTip(code: string): string {
        switch(code) {
            case 'UM.Auth':
                return 'um:auth.summary.$long';
            case 'CM.Case':
            case 'CM.CarePlanGoal':
            case 'CM.CarePlanIntervention':
            case 'CM.CarePlanBarrier':
            case 'CM.CarePlanOutcome':
            case 'CM.CaseTask':
                return 'cm:case.summary.$long';
            case 'CM.HouseholdCase':
                return `${code}.summary.$long`;
            case 'CS.MemberIssue':
            case 'CS.MemberIssueTask':
            case 'CS.ProviderIssue':
                return 'cs:issue.summary.$long';
            case 'AG.Case':
                return 'ag:case.summary.$long';
            case 'PM.Candidate':
            case 'PM.CandidateTask':
                return 'pm:candidate.summary.$long';
            case 'CD.Auth':
                return 'cd:auth.summary.$long';
            case 'CD.Claim':
                return 'cd:claim.summary.$long';
        }
        return "Code not Found";
    }

    /**
     * Generates the prefixes for attached to items based on their type codes
     * @param code (attachedTo.Type.code)
     * @returns description of a particular Module and Item based on a given code
     */
    public attachedToType(code: string): string {
        switch(code) {
            case 'UM.Auth':
                return 'um:auth.$long_module';
            case 'CM.Case':
            case 'CM.CarePlanGoal':
            case 'CM.CarePlanIntervention':
            case 'CM.CarePlanBarrier':
            case 'CM.CarePlanOutcome':
            case 'CM.CaseTask':
                return 'cm:case.$long_module';
            case 'CM.HouseholdCase':
                return 'cm:householdCase.$long_module';
            case 'CS.MemberIssue':
            case 'CS.MemberIssueTask':
            case 'CS.ProviderIssue':
                return 'cs:memberIssue.$_module';
            case 'AG.Case':
                return 'ag:case.$long_module';
            case 'PM.Candidate':
            case 'PM.CandidateTask':
                return 'pm:candidate.$long_module';
            case 'PM.Member':
                return 'pm:member.$long_module'
            case 'CD.Auth':
                return 'cd:auth.$long_module';
            case 'CD.Member':
                return 'cd:member.$';
            case 'CD.MemberPlan':
                return 'cd:member.$'; //Notes may not exist yet, adding in case
            case 'CD.Claim':
                return 'cd:claim.$';
        }
        return code;
    }

    /**
     * Determines if a user can see the summary link
     * @param accountContext Users account information
     * @param code (attachedTo.Type.code)
     * @returns if the user has permission to see/use the respective link
     */
    public hasPermission(accountContext: AccountContext, code: string): boolean {
        switch(code) {
            case 'UM.Auth':
                return accountContext.hasRouteAccess('um.auth.summary');
            case 'CM.Case':
            case 'CM.CarePlanGoal':
            case 'CM.CarePlanIntervention':
            case 'CM.CarePlanBarrier':
            case 'CM.CarePlanOutcome':
            case 'CM.CaseTask':
                return accountContext.hasRouteAccess('cm.case.summary');
            case 'CM.HouseholdCase':
                return accountContext.hasRouteAccess('cm.household-case.summary');
            case 'CS.MemberIssue':
            case 'CS.MemberIssueTask':
            case 'CS.ProviderIssue':
                return accountContext.hasRouteAccess(csIssueSummaryRouteID);
            case 'CS.Call':
                return accountContext.hasRouteAccess(csCallSummaryRouteID);
            case 'AG.Case':
                return accountContext.hasRouteAccess(agCaseSummaryRouteID);
            case 'PM.Candidate':
            case 'PM.CandidateTask':
                return accountContext.hasRouteAccess(pmCandidateSummaryRouteID);
            case 'CD.Auth':
                return accountContext.hasRouteAccess('cd.auth.summary');
            case 'CD.Claim':
                return accountContext.hasRouteAccess('cd.claim.summary');
        }
        return false;
    }

    /**
     * Opens the relevant summary based on a given code and respective ID
     * @param code (attachedTo.Type.code)
     * @param id (attachedTo.id)
     * @returns void. Just opens the respective summary
     */
    public async openAttachedToSummary(code: string, id: string, hasCaseRoute: boolean = false, hasAuthRoute: boolean = false, hasHouseholdCaseRoute: boolean = false): Promise<void> {

        switch(code) {
            case 'UM.Auth':
                umOpenAuthSummary(await this.loadUMAuth(id), hasAuthRoute, true);
                return;
            case 'CM.Case':
                cmOpenCaseSummary(await this.loadCMCase(id), hasCaseRoute);
                return;
            case 'CM.CarePlanGoal':
                cmOpenCaseSummary(await this.loadCMCaseByDetail('carePlan.goals', id), hasCaseRoute);
                return;
            case 'CM.CarePlanIntervention':
                cmOpenCaseSummary(await this.loadCMCaseByDetail('carePlan.goals.interventions', id), hasCaseRoute);
                return;
            case 'CM.CarePlanBarrier':
                cmOpenCaseSummary(await this.loadCMCaseByDetail('carePlan.goals.barriers', id), hasCaseRoute);
                return;
            case 'CM.CarePlanOutcome':
                cmOpenCaseSummary(await this.loadCMCaseByDetail('carePlan.goals.outcomes', id), hasCaseRoute);
                return;
            case 'CM.CaseTask':
                cmOpenCaseSummary(await this.loadCMCaseByDetail('tasks', id), hasCaseRoute);
                return;
            case 'CM.HouseholdCase':
                cmOpenHouseholdCaseSummary(await this.loadCMHouseholdCase(id), hasHouseholdCaseRoute);
                return;
            case 'CS.MemberIssue':
                this.transitional.openCSIssuePopup(id);
                return;
            case 'CS.MemberIssueTask':
                const issueId = (await this.loadCSMemberIssueByDetail('tasks', id)).id;
                this.transitional.openCSIssuePopup(issueId);
                return;
            case 'CS.ProviderIssue':
                this.transitional.openCSIssuePopup(id);
                return;
            case 'CS.Call':
                this.transitional.openCSCallSummaryPopup(id);
                return;
            case 'AG.Case':
                this.transitional.openAGCasePopup(id);
                return;
            case 'PM.Candidate':
                this.transitional.openPMCandidatePopup(id);
                return;
            case 'PM.CandidateTask':
                const candidateId = (await this.loadPMCandidateByDetail('tasks', id)).id;
                this.transitional.openPMCandidatePopup(candidateId);
                return;
            case 'CD.Auth':
                cdOpenAuthSummary(await this.loadCDAuth(id));
                return;
            case 'CD.Claim':
                cdOpenClaimSummary(await this.loadCDClaim(id));
                return;
        }
        return;
    }


    ////****************** Service Calls ********************************************////

    @apiCall
    private async loadUMAuth(authId: string): Promise<UM.Auth> {
        if(!authId) return {};
        const includes: string[] = [];

        try {
            this.state.beginLoad();

            const response = await this.umAuthService.loadAuth(authId, includes);
            return response.results[0];
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }

    @apiCall
    private async loadCMCase(caseId: string): Promise<CM.Case> {
        if(!caseId) return {};
        const includes: string[] = [];

        try {
            this.state.beginLoad();

            const response = await this.cmCaseService.loadCase(caseId, includes);
            return response.results[0];
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }

    @apiCall
    private async loadCMCaseByDetail(detailProperty: string, detailId: string): Promise<CM.Case> {
        if(!detailId) return {};
        const includes: string[] = [];

        try {
            this.state.beginLoad();

            const response = await this.cmCaseService.loadCaseByDetail(detailProperty, detailId, includes);
            return response.results[0];
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }

    @apiCall
    private async loadCMHouseholdCase(householdCaseId: string): Promise<CM.HouseholdCase> {
        if(!householdCaseId) return {};
        const includes: string[] = [];

        try {
            this.state.beginLoad();

            const response = await this.cmCaseService.loadHouseholdCase(householdCaseId, includes);
            return response.results[0];
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }

    @apiCall
    private async loadCSMemberIssueByDetail(detailProperty: string, detailId: string): Promise<CS.Issue> {
        if(!detailId) return {};
        const includes: string[] = [];

        try {
            this.state.beginLoad();

            const response = await this.csIssueService.loadIssueByDetail(detailProperty, detailId, includes);
            return response.results[0];
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }

    @apiCall
    private async loadPMCandidateByDetail(detailProperty: string, detailId: string): Promise<PM.Candidate> {
        if(!detailId) return {};
        const includes: string[] = [];

        try {
            this.state.beginLoad();

            const response = await this.pmCampaignService.loadCandidateByDetail(detailProperty, detailId, includes);
            return response.results[0];
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }

    @apiCall
    private async loadCDAuth(authId: string): Promise<CD.Auth> {
        if(!authId) return {};
        const includes: string[] = [];

        try {
            this.state.beginLoad();

            const response = await this.cdAuthService.loadAuth(authId, includes);
            return response.results[0];
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }

    @apiCall
    private async loadCDClaim(claimId: string): Promise<CD.Claim> {
        if(!claimId) return {};
        const includes: string[] = [];

        try {
            this.state.beginLoad();

            const response = await this.cdClaimService.loadClaim(claimId, includes);
            return response.results[0];
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }
}
